import React, { useEffect, useState } from 'react';
import { areaApi } from '../api/mockApi';

// The canonical list rarely changes, so fetch it once per page load and share it
// across every AreaSelect on the page rather than re-requesting per field.
let cache = null;
let inflight = null;

function loadAreas() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = areaApi.getAll()
      .then(res => {
        cache = res && res.success && res.data && Array.isArray(res.data.areas) ? res.data.areas : [];
        return cache;
      })
      .catch(() => { cache = []; return cache; })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/**
 * Area picker backed by the canonical reference list, grouped by borough.
 *
 * A record may already hold an area that is not in the list (geocoded from a
 * post code outside the usual patch). That value is added as its own option so
 * opening the form cannot silently change it.
 */
const AreaSelect = ({ value, onChange, id, includeBlank = true, blankLabel = 'Select area' }) => {
  const [areas, setAreas] = useState(cache || []);

  useEffect(() => {
    let alive = true;
    loadAreas().then(list => { if (alive) setAreas(list); });
    return () => { alive = false; };
  }, []);

  const boroughs = [...new Set(areas.map(a => a.borough).filter(Boolean))].sort();
  const known = areas.some(a => a.name === value);
  const current = String(value || '');

  return (
    <select id={id} value={current} onChange={e => onChange(e.target.value)}>
      {includeBlank && <option value="">{blankLabel}</option>}
      {current && !known && (
        <option value={current}>{current} (not in the standard list)</option>
      )}
      {boroughs.map(b => (
        <optgroup key={b} label={b}>
          {areas.filter(a => a.borough === b).map(a => (
            <option key={a.name} value={a.name}>{a.name}</option>
          ))}
        </optgroup>
      ))}
      {areas.filter(a => !a.borough).map(a => (
        <option key={a.name} value={a.name}>{a.name}</option>
      ))}
    </select>
  );
};

export default AreaSelect;
